import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppKit, useAppKitAccount, useAppKitProvider, useDisconnect } from '@reown/appkit/react';
import { fetchBalance, fetchTransactions, fetchCustodialInfo, fetchAllBalances } from '../store/walletSlice';
import CoinbaseOnrampWidget from '../components/CoinbaseOnrampWidget';
import CrossmintOnrampWidget from '../components/CrossmintOnrampWidget';
import metamaskLogo from '../assets/metamask.png';
import api from '../api';

export default function Wallet() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { transactions, loading, allBalances } = useSelector((state) => state.wallet);
  const { user } = useSelector((state) => state.auth);

  const { open: openWalletModal } = useAppKit();
  const { address: appKitAddress, isConnected: appKitConnected } = useAppKitAccount();
  const { walletProvider } = useAppKitProvider('eip155');
  const { disconnect: appKitDisconnect } = useDisconnect();

  const mmAddress = appKitConnected ? appKitAddress : '';
  const mmStatus = appKitConnected ? 'connected' : 'idle';

  const [activeTab, setActiveTab] = useState(null);
  const [expanding, setExpanding] = useState(null);
  const [copied, setCopied] = useState(false);
  const [custodialCopied, setCustodialCopied] = useState(false);

  const [depositAmount, setDepositAmount] = useState('');
  const [depositStatus, setDepositStatus] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawStatus, setWithdrawStatus] = useState('');
  const [custodialWithdrawAmount, setCustodialWithdrawAmount] = useState('');
  const [custodialWithdrawAddress, setCustodialWithdrawAddress] = useState('');
  const [custodialWithdrawStatus, setCustodialWithdrawStatus] = useState('');

  const [toWeb3Amount, setToWeb3Amount] = useState('');
  const [toWeb3Status, setToWeb3Status] = useState('');

  const [showHistory, setShowHistory] = useState(false);
  const [showTransferToWeb3, setShowTransferToWeb3] = useState(false);
  const [config, setConfig] = useState(null);
  const [showCoinbase, setShowCoinbase] = useState(false);
  const [showCrossmint, setShowCrossmint] = useState(false);
  const [showOnrampPicker, setShowOnrampPicker] = useState(false);

  useEffect(() => {
    if (location.state?.reset) {
      setActiveTab(null);
      setExpanding(null);
    }
  }, [location.state?.reset]);

  useEffect(() => {
    api.get('/config').then(({ data }) => setConfig(data)).catch(() => {});
    dispatch(fetchTransactions());
    dispatch(fetchAllBalances());
    dispatch(fetchCustodialInfo());
  }, [dispatch]);

  const connectWallet = () => {
    openWalletModal();
  };

  // Link wallet to account after connection
  const [walletLinkError, setWalletLinkError] = useState('');

  useEffect(() => {
    if (!appKitConnected || !walletProvider || !appKitAddress) {
      setWalletLinkError('');
      return;
    }
    // Only link if user doesn't already have this wallet linked
    if (user?.walletAddress?.toLowerCase() === appKitAddress.toLowerCase()) {
      setWalletLinkError('');
      return;
    }

    const linkWallet = async () => {
      try {
        const addr = appKitAddress;
        const message =
          `FiNema: link wallet ${addr.toLowerCase()} to my account\n\n` +
          `This wallet will be permanently linked. To change it later, contact support.`;

        const signature = await walletProvider.request({
          method: 'personal_sign',
          params: [message, addr],
        });

        await api.post('/auth/link-wallet', { address: addr, signature });
        setWalletLinkError('');
      } catch (err) {
        const status = err.response?.status;
        if (status === 409) {
          setWalletLinkError('This wallet is already linked to another account. Disconnect and use the correct account, or connect a different wallet.');
        } else if (status === 403) {
          setWalletLinkError('Your account already has a different wallet linked. Disconnect and connect that wallet instead.');
        } else {
          setWalletLinkError('');
        }
        console.error('Wallet linking error:', err);
      }
    };

    linkWallet();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appKitConnected, appKitAddress, walletProvider, user?.walletAddress]);

  const disconnect = () => {
    appKitDisconnect();
  };

  const waitForTx = async (txHash) => {
    for (let i = 0; i < 60; i++) {
      const receipt = await walletProvider.request({ method: 'eth_getTransactionReceipt', params: [txHash] });
      if (receipt) return receipt;
      await new Promise((r) => setTimeout(r, 2000));
    }
    return null;
  };

  const doDeposit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0 || !config) return;
    setDepositStatus('sending');
    try {
      const raw = Math.round(parseFloat(depositAmount) * 1e6);
      const amountHex = raw.toString(16).padStart(64, '0');
      const spender = config.finemaContract.toLowerCase().replace('0x', '').padStart(64, '0');

      const approveData = '0x095ea7b3' + spender + amountHex;
      const approveTx = await walletProvider.request({
        method: 'eth_sendTransaction',
        params: [{ from: mmAddress, to: config.usdcAddress, data: approveData }],
      });
      const approveReceipt = await waitForTx(approveTx);
      if (!approveReceipt || approveReceipt.status !== '0x1') {
        setDepositStatus('error');
        setTimeout(() => setDepositStatus(''), 4000);
        return;
      }

      // Wait for allowance state to propagate before deposit
      await new Promise((r) => setTimeout(r, 3000));

      const depositData = '0xb6b55f25' + amountHex;
      const depositTx = await walletProvider.request({
        method: 'eth_sendTransaction',
        params: [{ from: mmAddress, to: config.finemaContract, data: depositData, gas: '0x30D40' }],
      });
      const depositReceipt = await waitForTx(depositTx);

      if (depositReceipt && depositReceipt.status === '0x1') {
        setDepositStatus('sent');
        const savedAmount = depositAmount;
        setDepositAmount('');
        try { await api.post('/wallet/deposit', { txHash: depositTx, amount: parseFloat(savedAmount) }); } catch { /* non-critical: on-chain tx already succeeded */ }
        // Delay to let blockchain state propagate, then fetch twice
        await new Promise((r) => setTimeout(r, 2000));
        dispatch(fetchBalance());
        dispatch(fetchAllBalances());
        setTimeout(() => {
          dispatch(fetchBalance());
          dispatch(fetchAllBalances());
        }, 4000);
      } else {
        setDepositStatus('error');
      }
      setTimeout(() => setDepositStatus(''), 4000);
    } catch (err) {
      console.error('Deposit error:', err);
      setDepositStatus('error');
      setTimeout(() => setDepositStatus(''), 4000);
    }
  };

  const doWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0 || !config) return;
    setWithdrawStatus('sending');
    try {
      const raw = Math.round(parseFloat(withdrawAmount) * 1e6);
      const amountHex = raw.toString(16).padStart(64, '0');
      const data = '0x2e1a7d4d' + amountHex;

      const txHash = await walletProvider.request({
        method: 'eth_sendTransaction',
        params: [{ from: mmAddress, to: config.finemaContract, data }],
      });
      const receipt = await waitForTx(txHash);

      if (receipt && receipt.status === '0x1') {
        setWithdrawStatus('sent');
        setWithdrawAmount('');
        await new Promise((r) => setTimeout(r, 2000));
        dispatch(fetchBalance());
        dispatch(fetchAllBalances());
        setTimeout(() => {
          dispatch(fetchBalance());
          dispatch(fetchAllBalances());
        }, 4000);
      } else {
        setWithdrawStatus('error');
      }
      setTimeout(() => setWithdrawStatus(''), 4000);
    } catch (err) {
      console.error('Withdraw error:', err);
      setWithdrawStatus('error');
      setTimeout(() => setWithdrawStatus(''), 4000);
    }
  };

  const doCustodialWithdraw = async () => {
    if (!custodialWithdrawAmount || parseFloat(custodialWithdrawAmount) <= 0 || !custodialWithdrawAddress) return;
    setCustodialWithdrawStatus('sending');
    try {
      await api.post('/wallet/withdraw-external', {
        amount: parseFloat(custodialWithdrawAmount),
        toAddress: custodialWithdrawAddress,
      });
      setCustodialWithdrawStatus('sent');
      setCustodialWithdrawAmount('');
      setCustodialWithdrawAddress('');
      await new Promise((r) => setTimeout(r, 2000));
      dispatch(fetchCustodialInfo());
      dispatch(fetchAllBalances());
      dispatch(fetchTransactions());
      setTimeout(() => {
        dispatch(fetchCustodialInfo());
        dispatch(fetchAllBalances());
      }, 4000);
    } catch (err) {
      console.error('Custodial withdraw error:', err);
      setCustodialWithdrawStatus('error');
    }
    setTimeout(() => setCustodialWithdrawStatus(''), 4000);
  };

  const doTransferToWeb3 = async () => {
    if (!toWeb3Amount || parseFloat(toWeb3Amount) <= 0 || !config) return;
    setToWeb3Status('sending');
    try {
      await api.post('/wallet/transfer', {
        direction: 'custodial-to-metamask',
        amount: parseFloat(toWeb3Amount),
      });
      setToWeb3Status('sent');
      setToWeb3Amount('');
      await new Promise((r) => setTimeout(r, 2000));
      dispatch(fetchCustodialInfo());
      dispatch(fetchAllBalances());
      dispatch(fetchTransactions());
      setTimeout(() => {
        dispatch(fetchCustodialInfo());
        dispatch(fetchAllBalances());
      }, 4000);
      setTimeout(() => setToWeb3Status(''), 4000);
    } catch (err) {
      console.error('Transfer to web3 error:', err);
      setToWeb3Status('error');
      setTimeout(() => setToWeb3Status(''), 4000);
    }
  };

  const copyAddress = (addr, setter) => {
    if (!addr) return;
    navigator.clipboard.writeText(addr);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const shortAddr = (addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

  const CopyIcon = ({ copied: isCopied }) => isCopied ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
  );

  return (
    <div className="wallet-page">
      <h2>My Wallet</h2>

      {/* ===== WALLET SELECTOR ===== */}
      {!activeTab && (
        <div className={`wallet-selector ${expanding ? 'expanding' : ''}`}>
          <div
            className={`wallet-selector-card wallet-selector-primary ${expanding === 'web3' ? 'selected' : ''} ${expanding === 'normal' ? 'hidden' : ''}`}
            onClick={() => {
              if (expanding) return;
              setExpanding('web3');
              setTimeout(() => { setActiveTab('web3'); setExpanding(null); }, 400);
            }}
          >
            <div className="wallet-selector-icon">
              <img src={metamaskLogo} alt="MetaMask" style={{ width: 56, height: 56, objectFit: 'contain' }} />
            </div>
            <span className="wallet-selector-title">Web3 Wallet</span>
            <span className="wallet-selector-desc">MetaMask, WalletConnect, and more (for crypto users)</span>
          </div>

          <div
            className={`wallet-selector-card wallet-selector-secondary ${expanding === 'normal' ? 'selected' : ''} ${expanding === 'web3' ? 'hidden' : ''}`}
            onClick={() => {
              if (expanding) return;
              setExpanding('normal');
              setTimeout(() => { setActiveTab('normal'); setExpanding(null); }, 400);
            }}
          >
            <div className="wallet-selector-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#C8963E" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="18" rx="3" ry="3"/>
                <line x1="2" y1="9" x2="22" y2="9"/>
                <rect x="14" y="13" width="4" height="3" rx="1" fill="#C8963E" stroke="none"/>
              </svg>
            </div>
            <div className="wallet-selector-secondary-text">
              <span className="wallet-selector-title">Normal Wallet</span>
              <span className="wallet-selector-desc">Top up with card. Easy. (for everyone)</span>
            </div>
          </div>
        </div>
      )}

      {/* Back button */}
      {activeTab && (
        <button className="wallet-back-btn" onClick={() => setActiveTab(null)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back
        </button>
      )}

      {/* ===== NORMAL WALLET ===== */}
      {activeTab === 'normal' && (
        <div className="wallet-tab-content">
          {/* Custodial Address Card */}
          <div className="wallet-card">
            <div className="wallet-balance">
              <span className="wallet-balance-label">Balance</span>
              <span className="wallet-balance-amount">
                {(parseFloat(allBalances?.custodialBalance || 0) + parseFloat(allBalances?.custodialErc20 || 0)).toFixed(2)} $
              </span>
            </div>
            {user?.custodialAddress && (
              <div className="wallet-address-row">
                <span className="wallet-address-label">Custodial address</span>
                <div className="wallet-address-copy">
                  <code>{user.custodialAddress}</code>
                  <button className="wallet-copy-btn" onClick={() => copyAddress(user.custodialAddress, setCustodialCopied)} title="Copiar">
                    <CopyIcon copied={custodialCopied} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Normal wallet actions */}
          <div className="wallet-actions">
            <button className="wallet-action-btn" onClick={() => setShowOnrampPicker(true)}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C8963E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
              <span className="wallet-action-label">Deposit with card</span>
            </button>

            <button className="wallet-action-btn">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5B8C7A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20V4M5 11l7-7 7 7"/>
              </svg>
              <span className="wallet-action-label">Withdraw</span>
            </button>
          </div>

          {/* Onramp Provider Picker */}
          {showOnrampPicker && (
            <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowOnrampPicker(false); }}>
              <div className="modal-content onramp-picker-modal">
                <div className="modal-header">
                  <strong>Deposit with card</strong>
                  <button className="modal-close-btn" onClick={() => setShowOnrampPicker(false)}>&times;</button>
                </div>
                <p className="onramp-picker-subtitle">Choose a payment provider</p>
                <div className="onramp-picker-list">
                  <button className="onramp-picker-card" onClick={() => { setShowOnrampPicker(false); setShowCoinbase(true); }}>
                    <div className="onramp-picker-card-icon">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <rect width="32" height="32" rx="8" fill="#0052FF"/>
                        <path d="M16 6C10.48 6 6 10.48 6 16s4.48 10 10 10 10-4.48 10-10S21.52 6 16 6zm0 16.5a6.5 6.5 0 110-13 6.5 6.5 0 010 13zm2.78-8.25h-1.96v-1.96a.82.82 0 00-.82-.82-.82.82 0 00-.82.82v1.96h-1.96a.82.82 0 00-.82.82c0 .45.37.82.82.82h1.96v1.96c0 .45.37.82.82.82s.82-.37.82-.82v-1.96h1.96c.45 0 .82-.37.82-.82a.82.82 0 00-.82-.82z" fill="#fff"/>
                      </svg>
                    </div>
                    <div className="onramp-picker-card-content">
                      <span className="onramp-picker-name">Coinbase Onramp</span>
                      <span className="onramp-picker-desc">Recommended for USA, Canada, Australia, Europe</span>
                    </div>
                    <svg className="onramp-picker-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                  <button className="onramp-picker-card disabled" disabled>
                    <div className="onramp-picker-card-icon">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <rect width="32" height="32" rx="8" fill="#04B36E"/>
                        <path d="M16 7l-7 5v8l7 5 7-5v-8l-7-5zm0 2.2l4.8 3.4-4.8 3.4-4.8-3.4L16 9.2zm-5.4 4.6l4.6 3.3v5.7l-4.6-3.3v-5.7zm6.2 9l4.6-3.3v-5.7l-4.6 3.3v5.7z" fill="#fff"/>
                      </svg>
                    </div>
                    <div className="onramp-picker-card-content">
                      <span className="onramp-picker-name">Crossmint <span className="onramp-soon-tag">Soon</span></span>
                      <span className="onramp-picker-desc">Recommended for LATAM and others</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Custodial Withdraw Panel */}
          <div className="wallet-deposit-panel">
            <div className="wallet-deposit-header">
              <strong>Withdraw USDC to external wallet</strong>
            </div>
            <input
              type="text" placeholder="Wallet address (0x...)"
              value={custodialWithdrawAddress} onChange={(e) => setCustodialWithdrawAddress(e.target.value)}
              className="wallet-deposit-input" disabled={custodialWithdrawStatus === 'sending'}
              style={{ marginBottom: '8px', width: '100%' }}
            />
            <div className="wallet-deposit-row">
              <input
                type="number" step="0.01" min="0.01" placeholder="Amount USDC"
                value={custodialWithdrawAmount} onChange={(e) => setCustodialWithdrawAmount(e.target.value)}
                className="wallet-deposit-input" disabled={custodialWithdrawStatus === 'sending'}
              />
              <button className="btn btn-primary" onClick={doCustodialWithdraw} disabled={custodialWithdrawStatus === 'sending' || !custodialWithdrawAmount || !custodialWithdrawAddress}>
                {custodialWithdrawStatus === 'sending' ? 'Withdrawing...' : 'Withdraw'}
              </button>
            </div>
            {custodialWithdrawStatus === 'sent' && <div className="wallet-deposit-success">Withdrawal successful. USDC sent.</div>}
            {custodialWithdrawStatus === 'error' && <div className="wallet-deposit-error">Withdrawal error. Check your balance and address.</div>}
          </div>

          {/* Transfer to Web3 Wallet */}
          {mmAddress && (
            <>
              <button className="wallet-history-toggle" onClick={() => setShowTransferToWeb3(!showTransferToWeb3)}>
                <span>Send funds to Web3 Wallet</span>
                <svg className={`wallet-history-chevron ${showTransferToWeb3 ? 'open' : ''}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {showTransferToWeb3 && (
                <div className="wallet-deposit-panel" style={{ marginTop: 0, borderTop: 'none', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                  <div className="wallet-deposit-row">
                    <input
                      type="number" step="0.01" min="0.01" placeholder="Amount USDC"
                      value={toWeb3Amount} onChange={(e) => setToWeb3Amount(e.target.value)}
                      className="wallet-deposit-input" disabled={!!toWeb3Status}
                    />
                    <button className="btn btn-primary" onClick={doTransferToWeb3} disabled={!!toWeb3Status || !toWeb3Amount}>
                      {toWeb3Status === 'sending' ? 'Transferring...' : toWeb3Status === 'depositing' ? 'Depositing...' : 'Send'}
                    </button>
                  </div>
                  {toWeb3Status === 'sent' && <div className="wallet-deposit-success">Transfer successful.</div>}
                  {toWeb3Status === 'error' && <div className="wallet-deposit-error">Transfer error. Check your custodial balance.</div>}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ===== WEB3 WALLET TAB ===== */}
      {activeTab === 'web3' && (
        <div className="wallet-tab-content">
          {/* Balance Card */}
          <div className="wallet-card">
            <div className="wallet-balance">
              <span className="wallet-balance-label">Balance</span>
              <span className="wallet-balance-amount">
                {parseFloat(allBalances?.metamaskBalance || 0).toFixed(2)} $
              </span>
            </div>
            {mmAddress && (
              <div className="wallet-address-row">
                <span className="wallet-address-label">Wallet</span>
                <div className="wallet-address-copy">
                  <code>{mmAddress}</code>
                  <button className="wallet-copy-btn" onClick={() => copyAddress(mmAddress, setCopied)} title="Copiar">
                    <CopyIcon copied={copied} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="wallet-actions">
            <div className="wallet-action-col">
              <button className="wallet-action-btn" onClick={mmStatus === 'connected' ? undefined : connectWallet}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C8963E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="18" rx="3" ry="3"/>
                  <path d="M16 12h2" />
                  <circle cx="17" cy="12" r="1" fill="#C8963E" stroke="none"/>
                </svg>
                <span className="wallet-action-label">
                  {mmStatus === 'connected' ? shortAddr(mmAddress) : 'Connect Wallet'}
                </span>
              </button>
              {mmStatus === 'connected' && (
                <button className="wallet-disconnect-btn" onClick={disconnect}>Disconnect</button>
              )}
            </div>

            <button className="wallet-action-btn" onClick={() => navigate('/deposit-guide')}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5B8C7A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M2 12l10 10 10-10"/>
              </svg>
              <span className="wallet-action-label">Buy USDC</span>
            </button>

          </div>

          {/* Wallet link error */}
          {walletLinkError && (
            <div className="wallet-deposit-error" style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 8 }}>
              {walletLinkError}
            </div>
          )}

          {/* Web3 Guide */}
          <button className="wallet-history-toggle" onClick={() => navigate('/web3-guide')}>
            <span>New to crypto? Start here</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>

          {/* Deposit Panel */}
          {mmStatus === 'connected' && !walletLinkError && (
            <div className="wallet-deposit-panel">
              <div className="wallet-deposit-header">
                <strong>Deposit USDC in Finema</strong>
              </div>
              <div className="wallet-deposit-row">
                <input
                  type="number" step="0.01" min="0.01" placeholder="Amount USDC"
                  value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)}
                  className="wallet-deposit-input" disabled={depositStatus === 'sending'}
                />
                <button className="btn btn-primary" onClick={doDeposit} disabled={depositStatus === 'sending' || !depositAmount}>
                  {depositStatus === 'sending' ? 'Depositing...' : 'Deposit'}
                </button>
              </div>
              {depositStatus === 'sent' && <div className="wallet-deposit-success">Deposited successfully.</div>}
              {depositStatus === 'error' && <div className="wallet-deposit-error">Deposit error. Check your balance and network.</div>}
            </div>
          )}

          {/* Withdraw Panel */}
          {mmStatus === 'connected' && !walletLinkError && (
            <div className="wallet-deposit-panel">
              <div className="wallet-deposit-header">
                <strong>Withdraw USDC</strong>
              </div>
              <div className="wallet-deposit-row">
                <input
                  type="number" step="0.01" min="0.01" placeholder="Amount USDC"
                  value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)}
                  className="wallet-deposit-input" disabled={withdrawStatus === 'sending'}
                />
                <button className="btn btn-primary" onClick={doWithdraw} disabled={withdrawStatus === 'sending' || !withdrawAmount}>
                  {withdrawStatus === 'sending' ? 'Withdrawing...' : 'Withdraw'}
                </button>
              </div>
              {withdrawStatus === 'sent' && <div className="wallet-deposit-success">Withdrawal sent successfully.</div>}
              {withdrawStatus === 'error' && <div className="wallet-deposit-error">Withdrawal error. Check the address and your balance.</div>}
            </div>
          )}

          {/* Transfer to Normal Wallet — disabled until Normal Wallet is live */}
          {mmAddress && user?.custodialAddress && (
            <div className="wallet-coming-soon-section">
              <span className="wallet-coming-soon-label">Coming Soon</span>
              <button className="wallet-history-toggle" style={{ pointerEvents: 'none' }}>
                <span>Send funds to Normal Wallet</span>
                <svg className="wallet-history-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Transaction History (shared) */}
      <button className="wallet-history-toggle" onClick={() => setShowHistory(!showHistory)}>
        <span>Transaction history</span>
        <svg className={`wallet-history-chevron ${showHistory ? 'open' : ''}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {showHistory && (
        loading ? (
          <div className="loading">Loading...</div>
        ) : transactions.length === 0 ? (
          <div className="empty">No transactions yet</div>
        ) : (
          <table className="transactions-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
                <th>TX Hash</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id}>
                  <td>{tx.type}</td>
                  <td>{parseFloat(parseFloat(tx.amount_usdc).toFixed(2))} $</td>
                  <td><span className={`status-${tx.status}`}>{tx.status}</span></td>
                  <td><code className="tx-hash">{tx.tx_hash ? tx.tx_hash.slice(0, 10) + '...' : '-'}</code></td>
                  <td>{new Date(tx.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {/* Coinbase Onramp Widget Modal */}
      {showCoinbase && <CoinbaseOnrampWidget onClose={() => {
        setShowCoinbase(false);
        dispatch(fetchCustodialInfo());
        dispatch(fetchAllBalances());
      }} />}

      {showCrossmint && <CrossmintOnrampWidget onClose={() => {
        setShowCrossmint(false);
        dispatch(fetchCustodialInfo());
        dispatch(fetchAllBalances());
      }} />}
    </div>
  );
}
